import {
    Component, Input, OnInit
} from '@angular/core';

import { GenericValue } from 'hisng';

import { PublicationCreator } from 'app/fs/res/publication/model/publication-creator';
import { PublicationCreatorOrgunit } from 'app/fs/res/publication/model/publication-creator-orgunit';
import { ResearchPartnerSimple } from 'app/fs/res/common/model/researchPartner/researchPartner';
import { OrganizationService } from 'app/fs/res/common/service/organization/organization.service';

// custom type guard to check if variable is of type GenericValue
const isGenericValue = ( variableToCheck: any ): variableToCheck is GenericValue =>
    ( variableToCheck as GenericValue ).text !== undefined;

@Component( {
    selector: 'res-publication-creator-wizard-organization',
    templateUrl: './publication-creator-wizard-organization.component.html'
} )
export class PublicationCreatorWizardOrganizationComponent implements OnInit {
    @Input() creator: PublicationCreator;

    creatorOrganizations: PublicationCreatorOrgunit[] = [];
    internalOrganizations: GenericValue[] = [];
    externalOrganizations: ResearchPartnerSimple[] = [];
    externalOrgDialogVisible = false;

    private allHistorizedOrganizations: GenericValue[];

    constructor( private organizationService: OrganizationService ) {
        this.organizationService.getHistorized( '' ).subscribe( (organizations: GenericValue[]) => {
            this.allHistorizedOrganizations = organizations;
        } );
    }

    ngOnInit() {
        this.creatorOrganizations = this.creator.creatorOrganizations.map( x => Object.assign( {}, x ) );
        console.log( 'initial creatorOrganizations =', this.creatorOrganizations );
        this.creatorOrganizations.forEach( organization => {
            if ( !organization.sortorder ) {
                organization.sortorder = this.creatorOrganizations.length;
            }
            if ( organization.organization ) {
                this.internalOrganizations.push( organization.organization );
            } else if ( organization.researchPartner ) {
                this.externalOrganizations.push( organization.researchPartner );
            }
        } );
    }

    public selectInternalOrganization( event: GenericValue[] ) {
	    // #270613 Die Aktualisierung der Listen ist hier etwas komplizierter, weil historisierte interne Orgunits nur mit dem event
        // mitkommen wenn das Dropdown benutzt wurde; von der Baumauswahl hingegen werden Elemente, die nicht im Baum sind,
        // auch nicht ins event gesteckt. Es kann aber auch sein, dass nicht-historisierte interne Orgunits willentlich vom Anwender
        // in der Baumauswahl deselektiert wurden. Daher müssen wir den event mit der vorherigen Selektion (creatorOrganizations)
        // vergleichen und daraus ermitteln, welche Elemente tatsächlich vom Anwender in der Baumauswahl deselektiert wurden.
        // Historisierte und externe Orgunits müssen immer beibehalten werden.
        let newCreatorOrganizations: PublicationCreatorOrgunit[] = [];
        let newInternalOrganizations: GenericValue[] = [];

        this.creatorOrganizations.forEach( creatorOrgunit => {
            if ( creatorOrgunit.organization ) {
                // creatorOrgunit ist eine (möglicherweise historisierte) interne Orgunit
                let eventIndex: number = event.findIndex( eventOrgunit => creatorOrgunit.organization.id === eventOrgunit.id );
                if ( eventIndex < 0 ) {
                    // Die Orgunit wurde von der Baumauswahl entfernt. Wenn es sich um eine historisierte Orgunit handelt, dann
                    // wollen wir sie aber behalten, denn dann ist sie nur nicht im Event enthalten, weil sie kein Baumelement ist.
                    let historizedOrgunit = this.allHistorizedOrganizations.find( hOrg => hOrg.id === creatorOrgunit.organization.id );
                    if ( historizedOrgunit ) {
                        creatorOrgunit.sortorder = newCreatorOrganizations.length + 1;
                        newCreatorOrganizations.push( creatorOrgunit );
                        newInternalOrganizations.push( creatorOrgunit.organization );
                    } // else: Nicht historisierte interne Orgunit, die nicht im event ist, wird gedroppt!
                } else {
                    // Die Orgunit ist im event -> immer behalten
                    creatorOrgunit.sortorder = newCreatorOrganizations.length + 1;
                    newCreatorOrganizations.push( creatorOrgunit );
                    newInternalOrganizations.push( creatorOrgunit.organization );
                }
            } else {
                // researchPartners / externe Orgunits immer behalten
                creatorOrgunit.sortorder = newCreatorOrganizations.length + 1;
                newCreatorOrganizations.push( creatorOrgunit );
            }
        });
        this.creatorOrganizations = newCreatorOrganizations;
        this.internalOrganizations = newInternalOrganizations;

        // Neue interne Orgunits ans Ende der Listen anhängen
        event.forEach( eventOrgunit => {
            let index = this.creatorOrganizations.findIndex( cOrg => cOrg.organization && cOrg.organization.id === eventOrgunit.id );
            if ( index < 0 ) {
                this.internalOrganizations.push( eventOrgunit );
                this.addNewCreatorOrgunitToList( eventOrgunit );
            }
        });
        this.creatorOrganizations = this.creatorOrganizations.sort( ( a, b ) => a.sortorder - b.sortorder );
        this.copyOrganizationslistToCreator();
    }

    public selectExternalOrganization( event: ResearchPartnerSimple[] ) {
        this.externalOrganizations = [];
        event.forEach( researchPartner => {
            this.addNewCreatorOrgunitToList( researchPartner );
            this.externalOrganizations.push( researchPartner );
        } );
        this.creatorOrganizations = this.creatorOrganizations.sort( ( a, b ) => a.sortorder - b.sortorder ); // sort creatorOrganizations
        this.copyOrganizationslistToCreator();
    }

    private addNewCreatorOrgunitToList( organization: GenericValue | ResearchPartnerSimple ) {
        console.log( 'organization to add to list', organization );
        let creatorOrgunit = new PublicationCreatorOrgunit();
        let index = this.creatorOrganizations.findIndex( creatorOrg =>
            creatorOrg.organization === organization || creatorOrg.researchPartner === organization );
        if ( index === -1 ) {
            if ( this.creator.id ) {
                creatorOrgunit.creatorId = this.creator.id;
            }
            if ( isGenericValue( organization ) ) {
                creatorOrgunit.organization = organization;
            } else {
                creatorOrgunit.researchPartner = organization;
            }
            let newSortorder = this.creatorOrganizations.push( creatorOrgunit );
            this.creatorOrganizations[newSortorder - 1].sortorder = newSortorder;
            console.log( 'newest entry to organizations list', this.creatorOrganizations[newSortorder - 1] );
        }
    }

    // list manipulation methods ##########################################################

    /**
     * move list entry up
     *
     * @param organization organization to move up
     */
    public moveUp( organization: PublicationCreatorOrgunit ) {
        let newIndex = this.creatorOrganizations.findIndex( co => co.sortorder === ( organization.sortorder - 1 ) );
        organization.sortorder = organization.sortorder - 1;
        this.creatorOrganizations[newIndex].sortorder = this.creatorOrganizations[newIndex].sortorder + 1;
        this.creatorOrganizations = this.creatorOrganizations.sort( ( a, b ) => a.sortorder - b.sortorder ); // sort creatorOrganizations
        this.copyOrganizationslistToCreator();
    }

    /**
     * move list entry down
     *
     * @param organization organization to move down
     */
    public moveDown( organization: PublicationCreatorOrgunit ) {
        let newIndex = this.creatorOrganizations.findIndex( co => co.sortorder === ( organization.sortorder + 1 ) );
        organization.sortorder = organization.sortorder + 1;
        this.creatorOrganizations[newIndex].sortorder = this.creatorOrganizations[newIndex].sortorder - 1;
        this.creatorOrganizations = this.creatorOrganizations.sort( ( a, b ) => a.sortorder - b.sortorder ); // sort creatorOrganizations
        this.copyOrganizationslistToCreator();
    }

    /**
     * remove organization from list
     *
     * @param index index of organization to delete from list
     */
    public removeOrganization( organization: PublicationCreatorOrgunit ) {
        let newSortorder = 1;
        this.creatorOrganizations.splice( this.creatorOrganizations.findIndex(o => o === organization), 1 );
        if (organization.organization) {
            this.internalOrganizations = [...this.internalOrganizations.filter( o => o !== organization.organization )];
        }
        if (organization.researchPartner) {
            this.externalOrganizations.splice( this.externalOrganizations.findIndex(o => o === organization.researchPartner), 1 );
        }
        // set new sortorder for creatorOrganizations
        this.creatorOrganizations.forEach( co => { co.sortorder = newSortorder; newSortorder++; } );
        this.creatorOrganizations = this.creatorOrganizations.sort( ( a, b ) => a.sortorder - b.sortorder ); // sort creatorOrganizations
        this.copyOrganizationslistToCreator();
    }

    public createExternalOrgUnit() {
        this.externalOrgDialogVisible = true;
    }

    public saveDialogChange( event: ResearchPartnerSimple ) {
        this.addNewCreatorOrgunitToList( event );
        this.copyOrganizationslistToCreator();
        this.externalOrgDialogVisible = false;
    }

    private copyOrganizationslistToCreator() {
        this.creator.creatorOrganizations = this.creatorOrganizations.map( x => Object.assign( {}, x ) );
    }
}
